import { create } from 'zustand';
import { coreApi } from '@/integrations/api/active';
import { supabase } from '@/integrations/supabase/client';
import { useWorkspaceStore } from '@/modules/workspaces/workspace-store';
import {
  type NotificationCategory,
  type NotificationType,
  type CategoryPref,
  DEFAULT_NOTIFICATION_PREFS,
  ALWAYS_EMAIL_CATEGORIES,
  ALWAYS_IN_APP_CATEGORIES,
  notificationCategory,
} from '@/modules/notifications/types';

type PrefsMap = Partial<Record<NotificationCategory, CategoryPref>>;
// Per-recipient effective inApp flag: recipientEnabled[userId][category] = inApp boolean
type RecipientEnabled = Record<string, Partial<Record<NotificationCategory, boolean>>>;

const ALL_CATEGORIES = Object.keys(DEFAULT_NOTIFICATION_PREFS) as NotificationCategory[];
let _notificationPrefsRequestEpoch = 0;
let _recipientPrefsWorkspaceId: string | null = null;

interface NotificationPrefsState {
  globalPrefs: PrefsMap;
  workspacePrefsMap: Record<string, PrefsMap>;
  recipientEnabled: RecipientEnabled;
  loading: boolean;

  fetchGlobalPrefs: () => Promise<void>;
  fetchPrefs: (workspaceId: string) => Promise<void>;
  fetchWorkspaceMemberPrefs: (workspaceId: string, memberUserIds: string[]) => Promise<void>;
  updateGlobalPref: (category: NotificationCategory, field: keyof CategoryPref, value: boolean) => Promise<void>;
  updateWorkspacePref: (workspaceId: string, category: NotificationCategory, field: keyof CategoryPref, value: boolean) => Promise<void>;
  resetGlobalPrefs: () => Promise<void>;
  resetWorkspacePref: (workspaceId: string, category: NotificationCategory) => Promise<void>;
  resetAllWorkspacePrefs: (workspaceId: string) => Promise<void>;
  setWorkspacePrefsToCodeDefaults: (workspaceId: string) => Promise<void>;
  getEffectivePref: (workspaceId: string, category: NotificationCategory) => CategoryPref;
  isEnabled: (workspaceId: string, type: NotificationType, entityType?: string) => boolean;
  isRecipientEnabled: (recipientUserId: string, type: NotificationType, entityType?: string) => boolean;
  /** Drop everything on an account change (#142). */
  reset: () => void;
}

/** Notification-owned server cache, keyed by user and workspace. */
export const useNotificationPrefsStore = create<NotificationPrefsState>((set, get) => {
  // Sync recipientEnabled for a single user+category after state has been mutated.
  // Call AFTER the set() that mutates globalPrefs/workspacePrefsMap so get() reads fresh state.
  const syncRecipient = (userId: string, workspaceId: string, category: NotificationCategory) => {
    const inApp = get().getEffectivePref(workspaceId, category).inApp;
    set((s) => ({
      recipientEnabled: {
        ...s.recipientEnabled,
        [userId]: { ...s.recipientEnabled[userId], [category]: inApp },
      },
    }));
  };

  // Bulk version — refreshes all categories for a user.
  const syncRecipientAll = (userId: string, workspaceId: string) => {
    const userPrefs: Partial<Record<NotificationCategory, boolean>> = {};
    for (const cat of ALL_CATEGORIES) {
      userPrefs[cat] = get().getEffectivePref(workspaceId, cat).inApp;
    }
    set((s) => ({ recipientEnabled: { ...s.recipientEnabled, [userId]: userPrefs } }));
  };

  return {
    globalPrefs: {},
    workspacePrefsMap: {},
    recipientEnabled: {},
    loading: false,

    // Cleared on an account change (#142) — see AuthContext. `globalPrefs` is the
    // signed-in user's own settings, so carrying it across a user change shows
    // one person another's preferences and, worse, writes to them on the next
    // toggle. `recipientEnabled` is a cache about OTHER people and is rebuilt
    // from the same fetches, so it goes with them rather than lingering as a
    // half-valid map keyed by ids the new session may not be able to see.
    reset: () => {
      _notificationPrefsRequestEpoch += 1;
      _recipientPrefsWorkspaceId = null;
      set({ globalPrefs: {}, workspacePrefsMap: {}, recipientEnabled: {}, loading: false });
    },

    fetchGlobalPrefs: async () => {
      const requestEpoch = _notificationPrefsRequestEpoch;
      const { data: authData } = await supabase.auth.getUser();
      if (!authData.user) return;
      const { data } = await supabase
        .from('notification_preferences')
        .select('*')
        .eq('user_id', authData.user.id);
      if (requestEpoch !== _notificationPrefsRequestEpoch) return;
      const globalPrefs: PrefsMap = {};
      for (const row of data ?? []) {
        globalPrefs[row.category as NotificationCategory] = { inApp: row.in_app, email: row.email };
      }
      set({ globalPrefs });
    },

    fetchPrefs: async (workspaceId) => {
      const requestEpoch = _notificationPrefsRequestEpoch;
      set({ loading: true });
      const { data: authData } = await supabase.auth.getUser();
      if (!authData.user) { set({ loading: false }); return; }
      const userId = authData.user.id;

      const [globalResult, wsResult] = await Promise.all([
        supabase.from('notification_preferences').select('*').eq('user_id', userId),
        supabase
          .from('notification_workspace_preferences')
          .select('*')
          .eq('user_id', userId)
          .eq('workspace_id', workspaceId),
      ]);
      if (requestEpoch !== _notificationPrefsRequestEpoch) return;

      const globalPrefs: PrefsMap = {};
      for (const row of globalResult.data ?? []) {
        globalPrefs[row.category as NotificationCategory] = { inApp: row.in_app, email: row.email };
      }
      const wsPrefs: PrefsMap = {};
      for (const row of wsResult.data ?? []) {
        wsPrefs[row.category as NotificationCategory] = { inApp: row.in_app, email: row.email };
      }

      set((s) => ({
        globalPrefs,
        workspacePrefsMap: { ...s.workspacePrefsMap, [workspaceId]: wsPrefs },
        loading: false,
      }));
    },

    fetchWorkspaceMemberPrefs: async (workspaceId, memberUserIds) => {
      if (!memberUserIds.length) return;
      const requestEpoch = _notificationPrefsRequestEpoch;
      if (_recipientPrefsWorkspaceId !== workspaceId) set({ recipientEnabled: {} });
      const [wsResult, globalResult] = await Promise.all([
        supabase
          .from('notification_workspace_preferences')
          .select('user_id, category, in_app')
          .eq('workspace_id', workspaceId),
        supabase
          .from('notification_preferences')
          .select('user_id, category, in_app')
          .in('user_id', memberUserIds),
      ]);
      if (requestEpoch !== _notificationPrefsRequestEpoch) return;
      _recipientPrefsWorkspaceId = workspaceId;

      const map: RecipientEnabled = {};
      for (const row of globalResult.data ?? []) {
        (map[row.user_id] ??= {})[row.category as NotificationCategory] = row.in_app;
      }
      for (const row of (wsResult.data ?? []).filter((r) => memberUserIds.includes(r.user_id))) {
        (map[row.user_id] ??= {})[row.category as NotificationCategory] = row.in_app;
      }
      set({ recipientEnabled: map });
    },

    updateGlobalPref: async (category, field, value) => {
      const { data: authData } = await supabase.auth.getUser();
      if (!authData.user) return;
      const userId = authData.user.id;

      const current = get().globalPrefs[category] ?? DEFAULT_NOTIFICATION_PREFS[category];
      const updated: CategoryPref = { ...current, [field]: value };
      if (field === 'inApp' && !value) updated.email = false;

      set((s) => ({ globalPrefs: { ...s.globalPrefs, [category]: updated } }));

      // Propagate to recipientEnabled — effective value for current workspace after global change
      const wsId = useWorkspaceStore.getState().currentWorkspaceId;
      if (wsId) syncRecipient(userId, wsId, category);

      await coreApi('/notification-preferences', {
        preferences: [{ category, in_app: updated.inApp, email: updated.email }],
      });
    },

    updateWorkspacePref: async (workspaceId, category, field, value) => {
      const { data: authData } = await supabase.auth.getUser();
      if (!authData.user) return;
      const userId = authData.user.id;

      const existing = get().workspacePrefsMap[workspaceId]?.[category];
      const base = existing ?? get().getEffectivePref(workspaceId, category);
      const updated: CategoryPref = { ...base, [field]: value };
      if (field === 'inApp' && !value) updated.email = false;

      set((s) => ({
        workspacePrefsMap: {
          ...s.workspacePrefsMap,
          [workspaceId]: { ...s.workspacePrefsMap[workspaceId], [category]: updated },
        },
      }));

      // Workspace override is always the effective value — sync directly
      set((s) => ({
        recipientEnabled: {
          ...s.recipientEnabled,
          [userId]: { ...s.recipientEnabled[userId], [category]: updated.inApp },
        },
      }));

      await coreApi('/notification-preferences', {
        workspace_id: workspaceId,
        preferences: [{ category, in_app: updated.inApp, email: updated.email }],
      });
    },

    resetGlobalPrefs: async () => {
      const { data: authData } = await supabase.auth.getUser();
      if (!authData.user) return;
      const userId = authData.user.id;

      set({ globalPrefs: {} });

      const wsId = useWorkspaceStore.getState().currentWorkspaceId;
      if (wsId) syncRecipientAll(userId, wsId);

      await coreApi('/notification-preferences', {}, 'DELETE');
    },

    resetWorkspacePref: async (workspaceId, category) => {
      const { data: authData } = await supabase.auth.getUser();
      if (!authData.user) return;
      const userId = authData.user.id;

      set((s) => {
        const wsMap = { ...(s.workspacePrefsMap[workspaceId] ?? {}) };
        delete wsMap[category];
        return { workspacePrefsMap: { ...s.workspacePrefsMap, [workspaceId]: wsMap } };
      });

      // After removing workspace row, effective = global → propagate that value
      syncRecipient(userId, workspaceId, category);

      await coreApi('/notification-preferences', { workspace_id: workspaceId, category }, 'DELETE');
    },

    resetAllWorkspacePrefs: async (workspaceId) => {
      const { data: authData } = await supabase.auth.getUser();
      if (!authData.user) return;
      const userId = authData.user.id;

      set((s) => ({ workspacePrefsMap: { ...s.workspacePrefsMap, [workspaceId]: {} } }));

      // After clearing workspace overrides, effective = global for all categories
      syncRecipientAll(userId, workspaceId);

      await coreApi('/notification-preferences', { workspace_id: workspaceId }, 'DELETE');
    },

    setWorkspacePrefsToCodeDefaults: async (workspaceId) => {
      const { data: authData } = await supabase.auth.getUser();
      if (!authData.user) return;
      const userId = authData.user.id;

      const defaults: PrefsMap = {};
      for (const cat of ALL_CATEGORIES) defaults[cat] = DEFAULT_NOTIFICATION_PREFS[cat];

      set((s) => ({ workspacePrefsMap: { ...s.workspacePrefsMap, [workspaceId]: defaults } }));

      // After setting to code defaults, effective = code defaults for all categories
      syncRecipientAll(userId, workspaceId);

      await coreApi('/notification-preferences', {
        workspace_id: workspaceId,
        preferences: ALL_CATEGORIES.map((cat) => ({
          category: cat,
          in_app: DEFAULT_NOTIFICATION_PREFS[cat].inApp,
          email: DEFAULT_NOTIFICATION_PREFS[cat].email,
        })),
      });
    },

    getEffectivePref: (workspaceId, category) => {
      const pref = get().workspacePrefsMap[workspaceId]?.[category]
        ?? get().globalPrefs[category]
        ?? DEFAULT_NOTIFICATION_PREFS[category];
      return {
        inApp: ALWAYS_IN_APP_CATEGORIES.includes(category) ? true : pref.inApp,
        email: ALWAYS_EMAIL_CATEGORIES.includes(category) ? true : pref.email,
      };
    },

    isEnabled: (workspaceId, type, entityType) => {
      const cat = notificationCategory(type, entityType);
      return get().getEffectivePref(workspaceId, cat).inApp;
    },

    isRecipientEnabled: (recipientUserId, type, entityType) => {
      const cat = notificationCategory(type, entityType);
      if (ALWAYS_IN_APP_CATEGORIES.includes(cat)) return true;
      const userPrefs = get().recipientEnabled[recipientUserId];
      if (!userPrefs) return DEFAULT_NOTIFICATION_PREFS[cat].inApp;
      const val = userPrefs[cat];
      return val !== undefined ? val : DEFAULT_NOTIFICATION_PREFS[cat].inApp;
    },
  };
});
